import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CircleCheck, Trash2, X } from 'lucide-react'
import { cn, formatCurrency } from '@utils'
import { OVERLAY_FLOATING_Z } from '@utils/overlayStack'
import { ImageSize } from '@/constants'
import OmpCrossSellImage from './OmpCrossSellImage'

export const CrossSellNoticeKind = {
  Added: 'added',
  Removed: 'removed',
} as const
type NoticeKind = (typeof CrossSellNoticeKind)[keyof typeof CrossSellNoticeKind]

export interface CrossSellCartNotice {
  kind: NoticeKind
  name: string
  quantity: number
  // Line total for `quantity`.
  total: number
  currency?: string
  // Campaign product the thumbnail is resolved from.
  imageSource: any
  // Unique per notice, so a repeat of the same product restarts the timer.
  stamp: number
}

interface OmpCrossSellAddedToastProps {
  notice: CrossSellCartNotice | null
  onViewCart: () => void
  onClose: () => void
}

const AUTO_CLOSE_MS = 4000

const TITLES: Record<NoticeKind, string> = {
  [CrossSellNoticeKind.Added]: 'Added to cart',
  [CrossSellNoticeKind.Removed]: 'Removed from cart',
}

// Cart add / remove confirmation with the product, plus a shortcut to the cart after an add. The
// shared toast takes only a title and message, so the cross-sell keeps its own.
const OmpCrossSellAddedToast = ({ notice, onViewCart, onClose }: OmpCrossSellAddedToastProps) => {
  useEffect(() => {
    if (!notice) {
      return
    }
    const timer = setTimeout(onClose, AUTO_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [notice, onClose])

  if (!notice) {
    return null
  }

  const added = notice.kind === CrossSellNoticeKind.Added
  const label = notice.quantity > 1 ? `${notice.quantity} × ${notice.name}` : notice.name

  // Removes always fire with the cart drawer open, and View All stays open after an add, so the
  // notification must sit above the overlay stack to be seen and clicked.
  return createPortal(
    <div
      className={cn('omp-crosssell-toast', !added && 'omp-crosssell-toast-center')}
      style={{ zIndex: OVERLAY_FLOATING_Z }}
      role="status"
      aria-live="polite">
      <div className="omp-crosssell-toast-thumb">
        {/* The card's size, so the image is already cached and shows with the notification. */}
        <OmpCrossSellImage source={notice.imageSource} size={ImageSize.Original} alt={notice.name} />
      </div>
      <div className="omp-crosssell-toast-body">
        <p className={cn('omp-crosssell-toast-title', !added && 'omp-crosssell-toast-title-removed')}>
          {added ? <CircleCheck size={18} aria-hidden="true" /> : <Trash2 size={18} aria-hidden="true" />}
          {TITLES[notice.kind]}
        </p>
        <p className="omp-crosssell-toast-line">
          {label} · {formatCurrency(notice.total, notice.currency)}
        </p>
      </div>
      {added && (
        <button type="button" className="omp-crosssell-toast-action" onClick={onViewCart}>
          View Cart
        </button>
      )}
      <button type="button" className="omp-crosssell-toast-close" aria-label="Dismiss" onClick={onClose}>
        <X size={20} />
      </button>
    </div>,
    document.body
  )
}

export default OmpCrossSellAddedToast
